import { GithubClient } from 'api/clients/GithubClient'
import { useInterval } from 'beautiful-react-hooks'
import { StrongNode, nodes } from 'prose-core'
import * as React from 'react'
import { useQuery } from 'react-query'
import store from 'redux/store'
import { FetchError } from './common'
import { GithubCard } from './GithubCard'
import { EmbedError } from '../EmbedCommon/EmbedError'
import { EmbedPrivate } from '../EmbedCommon/EmbedPrivate'
import { GithubFrame } from './GithubFrame'
import { GithubInline } from './GithubInline'
import { PullResponse } from './types'
import { ReactComponent as GithubIcon } from 'assets/icons/logos/github24.svg'

export type GithubPullProps = {
  node: StrongNode<typeof nodes.github_embed>
  preview?: true
}

export const GithubPull: React.FC<React.PropsWithChildren<GithubPullProps>> = (props) => {
  const { node, preview } = props
  const { repo, id } = /(?<repo>[^/]+?\/[^/]+?)\/[^/]+?\/(?<id>[^/]+?)$/.exec(node.attrs.link)?.groups ?? {}
  const [shouldRetry, setShouldRetry] = React.useState(false)
  const maybeArticle = store.getState().currentArticleId
    ? // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      store.getState().articleMeta[store.getState().currentArticleId!]
    : null

  const { isLoading, data, isError, refetch, error } = useQuery(
    [maybeArticle?.company_id, 'pull', repo, id],
    async () => {
      if (!maybeArticle?.company_id) {
        throw new Error('missing company_id (are you inside a workspace?)')
      }
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const githubClientResponse: any = await GithubClient.getResource(maybeArticle!.company_id!, 'pull', repo, id)
      if (githubClientResponse.status >= 400) {
        throw new FetchError(githubClientResponse, 'Error')
      }
      return githubClientResponse.data as PullResponse
    },
    {
      staleTime: 10 * 60 * 1000, // 10 minutes
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: () => {
        if (isError) {
          // if the request is not successful, refetchOnWindowFocus only if the timeout has elapsed
          if (shouldRetry) {
            setShouldRetry(false)
            return true
          } else {
            return false
          }
        }
        // if the request is successful, respect staleTime
        return true
      },
    },
  )

  useInterval(() => {
    if (isError) {
      setShouldRetry(true)
    }
  }, 10 * 60 * 1000)

  if (isError && [401, 404].includes((error as FetchError)?.res?.status)) {
    return <EmbedPrivate mode={node.attrs.mode} link={node.attrs.link} preview={preview} icon={GithubIcon} />
  } else if (isError || (!isLoading && !data)) {
    return <EmbedError refetch={refetch} mode={node.attrs.mode} link={node.attrs.link} preview={preview} />
  }

  const status = (() => {
    switch (data?.state) {
      case 'open':
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        if (data!.draft) return 'draft'
        return 'open'
      case 'closed':
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        if (data!.merged_at) return 'merged'
        return 'closed'
      default:
        return 'open'
    }
  })()

  switch (node?.attrs.mode) {
    default:
    case 'frame':
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return <GithubFrame preview={preview} node={node} data={data!} isLoading={isLoading} status={status} />
    case 'card':
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return <GithubCard preview={preview} node={node} data={data!} isLoading={isLoading} status={status} />
    case 'inline':
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return <GithubInline preview={preview} node={node} data={data!} isLoading={isLoading} status={status} />
  }
}
